import React, { useEffect, useState } from 'react';
import { getOffSeasonMode, updateOffSeasonMode } from '../../adminApi';
import styles from '../../styles/AdminDashboard.module.css';

const MODE_LABELS = {
  auto: 'Auto (follow schedule)',
  on: 'Force on (reduced load)',
  off: 'Force off (normal load)',
};

const REASON_LABELS = {
  manual_on: 'Manually enabled',
  manual_off: 'Manually disabled',
  auto_schedule_active: 'Auto schedule (off-season window)',
  auto_schedule_inactive: 'Auto schedule (in-season window)',
};

const OffSeasonModePanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState({
    mode: 'auto',
    autoStart: '02-01',
    autoEnd: '08-15',
    active: false,
    effectiveReason: '',
  });

  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await getOffSeasonMode();
      setStatus(response.data);
    } catch (err) {
      console.error('Error loading off-season mode:', err);
      setMessage('Failed to load off-season mode settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const response = await updateOffSeasonMode({
        mode: status.mode,
        autoStart: status.autoStart,
        autoEnd: status.autoEnd,
      });
      setStatus(response.data);
      setMessage(`Saved. Off-season mode is currently ${response.data.active ? 'active' : 'inactive'}.`);
    } catch (err) {
      console.error('Error saving off-season mode:', err);
      setMessage(err.response?.data?.error || 'Failed to save off-season mode settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p>Loading off-season mode...</p>;
  }

  return (
    <div className={styles.settingsSection}>
      <h4>Off-Season Mode</h4>
      <p className={styles.settingDescription}>
        Reduces background work when the league is idle (~Feb–Aug by default). Skips 15-minute
        dynamic-week checks, pick reminders, live scoreboard polling when no games are in progress,
        and other heavy scheduled tasks. API Saver Mode on the API Status panel still applies
        additional API call reductions when enabled.
      </p>

      <p className={styles.settingDescription}>
        <strong>Current state:</strong>{' '}
        {status.active ? 'Active (reduced load)' : 'Inactive (normal load)'}
        {' — '}
        {REASON_LABELS[status.effectiveReason] || status.effectiveReason}
      </p>

      <div className={styles.settingRow}>
        <label className={styles.settingLabel}>
          Control mode:
          <select
            value={status.mode}
            onChange={(e) => setStatus((prev) => ({ ...prev, mode: e.target.value }))}
            disabled={saving}
            className={styles.weekSelect}
          >
            {Object.entries(MODE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className={styles.settingRow}>
        <label className={styles.settingLabel}>
          Auto-start (MM-DD, inclusive):
          <input
            type="text"
            value={status.autoStart}
            onChange={(e) => setStatus((prev) => ({ ...prev, autoStart: e.target.value }))}
            disabled={saving}
            placeholder="02-01"
            pattern="\d{2}-\d{2}"
            className={styles.weekSelect}
          />
        </label>
      </div>

      <div className={styles.settingRow}>
        <label className={styles.settingLabel}>
          Auto-end (MM-DD, exclusive):
          <input
            type="text"
            value={status.autoEnd}
            onChange={(e) => setStatus((prev) => ({ ...prev, autoEnd: e.target.value }))}
            disabled={saving}
            placeholder="08-15"
            pattern="\d{2}-\d{2}"
            className={styles.weekSelect}
          />
        </label>
      </div>

      {message && (
        <div className={`${styles.updateMessage} ${message.includes('Failed') ? styles.error : styles.success}`}>
          {message}
        </div>
      )}

      <div className={styles.actionButtons}>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className={styles.saveButton}
        >
          {saving ? 'Saving...' : 'Save Off-Season Settings'}
        </button>
      </div>
    </div>
  );
};

export default OffSeasonModePanel;
